import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { PatrolPolicyPresetId } from '../../../models/patrol-discord-cockpit.model';
import { PatrolDiscordCockpitService } from '../../../services/patrol-discord-cockpit.service';
import { PATROL_KIND_SPEECH_DEFAULT_I18N_PARAMS } from '../../../constants/patrol-kind-speech-defaults';

const GUILD_DEFAULT_PRESETS: ReadonlyArray<PatrolPolicyPresetId> = [
  'relaxed',
  'standard',
  'strict',
];

const ROLE_PRESETS = [
  'inherit',
  'bypass',
  'relaxed',
  'standard',
  'strict',
] as const;

const CHANNEL_PRESETS = [
  'inherit',
  'off',
  'relaxed',
  'standard',
  'strict',
];

const CHANNEL_ACCESS_MODES = [
  'inherit',
  'open',
  'linked_only',
  'age_bracket',
] as const;

@Component({
  selector: 'app-patrol-discord-policies',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './patrol-discord-policies.component.html',
  styleUrl: './patrol-discord-policies.component.scss',
})
export class PatrolDiscordPoliciesComponent {
  #cockpit = inject(PatrolDiscordCockpitService);

  readonly settings = this.#cockpit.guildSettings;
  readonly discordRoles = this.#cockpit.discordRoles;
  readonly discordChannels = this.#cockpit.discordChannels;
  readonly isLoadingPolicies = this.#cockpit.isLoadingPolicies;
  readonly isSavingPolicies = this.#cockpit.isSavingPolicies;
  readonly policiesError = this.#cockpit.policiesError;
  readonly guildDefaultPresets = GUILD_DEFAULT_PRESETS;
  readonly rolePresets = ROLE_PRESETS;
  readonly channelPresets = CHANNEL_PRESETS;
  readonly channelAccessModes = CHANNEL_ACCESS_MODES;
  readonly kindSpeechDefaultParams = PATROL_KIND_SPEECH_DEFAULT_I18N_PARAMS;

  moderatedChannels() {
    return this.discordChannels().filter(
      (channel) => channel.channelType !== 'voice',
    );
  }

  rolePolicyValue(discordRoleId: string): string {
    return this.#cockpit.rolePolicyForRole(discordRoleId);
  }

  channelPolicyValue(discordChannelId: string): string {
    return this.#cockpit.channelPolicyForChannel(discordChannelId);
  }

  channelAccessValue(discordChannelId: string): string {
    return this.#cockpit.channelAccessForChannel(discordChannelId);
  }

  channelAccessAge(discordChannelId: string) {
    return this.#cockpit.channelAccessAgeForChannel(discordChannelId);
  }

  presetDescriptionKey(
    presetId: PatrolPolicyPresetId,
  ): string | null {
    if (presetId === 'relaxed' || presetId === 'standard' || presetId === 'strict') {
      return `app.patrol.discord.cockpit.presets.descriptions.${presetId}`;
    }
    return null;
  }

  onDefaultPolicyChange(value: string): void {
    void this.#cockpit.updateDefaultPolicy(value as PatrolPolicyPresetId);
  }

  onToggle(
    key:
      | 'linkRequired'
      | 'reputationEnabled'
      | 'failClosedOnAiOutage'
      | 'notificationsEmail'
      | 'kindSpeechEnabled',
    checked: boolean,
  ): void {
    void this.#cockpit.updateGuildSetting(key, checked);
  }

  onVerifiedRoleNameChange(value: string): void {
    void this.#cockpit.updateGuildSetting(
      'verifiedRoleName',
      value.trim() || 'Verified',
    );
  }

  onChannelAccessChange(discordChannelId: string, value: string): void {
    if (value === 'inherit' || value === 'open' || value === 'linked_only') {
      void this.#cockpit.updateChannelAccess(discordChannelId, value);
      return;
    }

    if (value === 'age_bracket') {
      this.#cockpit.stageChannelAccessAgeBracket(discordChannelId);
    }
  }

  hasChannelAccessAgeDraft(discordChannelId: string): boolean {
    return this.#cockpit.hasChannelAccessAgeDraft(discordChannelId);
  }

  onChannelAccessAgeChange(
    discordChannelId: string,
    field: 'minAge' | 'maxAge',
    value: string | number,
  ): void {
    this.#cockpit.setChannelAccessAgeDraft(discordChannelId, field, value);
  }

  onConfirmChannelAccessAge(discordChannelId: string): void {
    void this.#cockpit.confirmChannelAccessAge(discordChannelId);
  }

  onRolePolicyChange(discordRoleId: string, value: string): void {
    void this.#cockpit.updateRolePolicy(
      discordRoleId,
      value as PatrolPolicyPresetId | 'inherit',
    );
  }

  onChannelPolicyChange(discordChannelId: string, value: string): void {
    void this.#cockpit.updateChannelPolicy(
      discordChannelId,
      value as PatrolPolicyPresetId | 'inherit',
    );
  }
}
